import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, LogOut, PlayCircle, Lock, BookOpen, Headphones, Sparkles, Users, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasAccess = user && user.has_making_fabulous_access;
  const hasTmi = user && user.has_menopause_invitation_access;
  const hasMensGuide = user && user.has_mens_guide_access;
  const isPrivateClient = user && user.is_private_client;
  const isCoCreatorsMember = user && user.is_co_creators_member;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const endpoint = hasAccess ? "/program/modules" : "/program/preview";
        const { data } = await api.get(endpoint);
        if (!cancelled) setModules(data);
      } catch {
        if (!cancelled) setModules([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [hasAccess]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-dashboard">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 bg-cream-soft border-r border-hairline flex-col">
        <div className="p-8 border-b border-hairline">
          <Link to="/" className="font-serif-display text-2xl text-ink">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>
          <div className="mt-6">
            <div className="eyebrow text-ink-soft">Signed in as</div>
            <div className="mt-1 text-ink font-medium truncate" data-testid="sidebar-user">{user?.name || user?.email}</div>
          </div>
        </div>
        <nav className="flex-1 p-6 space-y-2">
          <div className="eyebrow text-ink-soft mb-4 px-3">Program</div>
          <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-sage/10 text-sage font-medium" data-testid="sidebar-overview">
            <Sparkles size={16} strokeWidth={1.5} /> Overview
          </Link>
          {(modules || []).map((m) => (
            <Link
              key={m.id}
              to={hasAccess ? `/dashboard/module/${m.id}` : "/making-fabulous"}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-ink-soft hover:bg-cream hover:text-ink transition text-sm"
              data-testid={`sidebar-module-${m.id}`}
            >
              {hasAccess ? <PlayCircle size={14} strokeWidth={1.5} /> : <Lock size={14} strokeWidth={1.5} />}
              <span className="truncate">{m.title}</span>
            </Link>
          ))}
        </nav>
        <div className="p-6 border-t border-hairline">
          {user?.role === "admin" && (
            <Link
              to="/admin"
              className="flex items-center gap-3 text-ink-soft hover:text-sage text-sm w-full mb-4"
              data-testid="sidebar-admin-link"
            >
              <Sparkles size={14} strokeWidth={1.5} /> Admin console
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 text-ink-soft hover:text-ink text-sm w-full"
            data-testid="sidebar-logout"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-5xl">
          {/* mobile header */}
          <div className="lg:hidden flex items-center justify-between mb-10">
            <Link to="/" className="font-serif-display text-xl text-ink">
              Pip<span className="text-gold italic"> Bennett</span>
            </Link>
            <button onClick={handleLogout} className="text-ink-soft text-sm" data-testid="mobile-logout">Sign out</button>
          </div>

          <div className="eyebrow mb-4 text-sage">Welcome back</div>
          <h1 className="font-serif-display text-4xl md:text-6xl text-ink leading-[1.05]" data-testid="dashboard-title">
            Hello, <em className="italic text-sage">{user?.name || "friend"}</em>.
          </h1>
          {isPrivateClient && (
            <div
              className="mt-6 inline-flex items-center gap-2 bg-gold/15 border border-gold/40 text-ink rounded-full px-5 py-2 text-sm font-medium"
              data-testid="dashboard-private-badge"
            >
              <Sparkles size={14} strokeWidth={1.8} className="text-gold-dark" />
              Welcome to your private coaching space.
            </div>
          )}
          <p className="mt-6 text-ink-soft max-w-xl leading-relaxed">
            {isPrivateClient
              ? "Everything in the program library is yours. Begin wherever feels right — and we'll keep building together in our private sessions."
              : hasAccess
              ? "Everything you need is here. Begin wherever feels right — there's no wrong way to walk this path."
              : "You're almost in. Unlock the full program to access the video modules, workbooks, and guided meditations."}
          </p>

          {!hasAccess && (
            <div className="mt-10 bg-sage text-cream rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between" data-testid="dashboard-upgrade">
              <div>
                <div className="eyebrow mb-2 text-gold">Pre-launch</div>
                <h2 className="font-serif-display text-2xl md:text-3xl">The Rewired Life Waitlist</h2>
                <p className="mt-3 text-cream/75 max-w-md">
                  Doors are opening soon — Pip is recording the final teaching pieces. Add your name and you'll be first through the door at the founding $197 AUD lifetime price.
                </p>
              </div>
              <Link
                to="/the-rewired-life"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-8 py-4 font-medium whitespace-nowrap transition-all"
                data-testid="dashboard-upgrade-cta"
              >
                Join Waitlist
                <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
            </div>
          )}

          {isCoCreatorsMember && (
            <div
              className="mt-8 rounded-3xl border-2 border-gold/40 bg-gradient-to-br from-gold/10 via-cream to-gold/5 p-7 md:p-8 shadow-[0_22px_55px_-25px_rgba(201,169,97,0.45)]"
              data-testid="dashboard-co-creators-banner"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-8">
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-2 bg-gold/20 text-gold-dark px-3 py-1 rounded-full text-[0.65rem] tracking-[0.18em] uppercase font-bold mb-4">
                    <Sparkles size={11} strokeWidth={2.4} /> Co-Creators Circle
                  </div>
                  <h2 className="font-serif-display text-2xl md:text-3xl text-ink leading-snug">
                    Welcome to the Circle, <em className="italic text-gold-dark">{user?.name?.split(" ")[0] || "friend"}</em>.
                  </h2>
                  <p className="mt-4 text-ink-soft leading-relaxed max-w-xl">
                    You&apos;re in. Your dashboard below holds the full program — six modules, the auto-saving workbook,
                    and your live session details. As a co-creator, your reflections shape how this work lands for
                    the women who come after you.
                  </p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                    <div className="bg-cream/60 border border-gold/25 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.15em] uppercase text-gold-dark font-semibold mb-1">
                        <PlayCircle size={11} strokeWidth={2.2} /> Modules
                      </div>
                      <div className="text-xs text-ink-soft leading-snug">Six self-paced video modules</div>
                    </div>
                    <div className="bg-cream/60 border border-gold/25 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.15em] uppercase text-gold-dark font-semibold mb-1">
                        <BookOpen size={11} strokeWidth={2.2} /> Workbook
                      </div>
                      <div className="text-xs text-ink-soft leading-snug">Auto-saves as you go</div>
                    </div>
                    <div className="bg-cream/60 border border-gold/25 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.15em] uppercase text-gold-dark font-semibold mb-1">
                        <Users size={11} strokeWidth={2.2} /> Live cohort
                      </div>
                      <div className="text-xs text-ink-soft leading-snug">Zoom session details inside</div>
                    </div>
                  </div>

                  <div className="mt-6 flex items-start gap-2.5 bg-cream/70 border border-hairline rounded-2xl px-4 py-3 max-w-2xl">
                    <ClipboardList size={14} strokeWidth={2} className="text-gold-dark flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-ink-soft leading-relaxed">
                      <span className="font-semibold text-ink">Your three feedback forms</span> — one before you begin,
                      one during, and one when you complete the program. I&apos;ll email each one at the right moment;
                      you don&apos;t need to chase them.
                    </p>
                  </div>

                  <div className="mt-7">
                    <Link
                      to="/dashboard/menopause"
                      className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-7 py-3.5 font-semibold text-sm md:text-[0.95rem] transition-all"
                      data-testid="dashboard-co-creators-cta"
                    >
                      Open The Menopause Invitation
                      <ArrowRight size={15} strokeWidth={2.2} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {hasTmi && !isCoCreatorsMember && (
            <div className="mt-6 bg-cream-soft border border-hairline rounded-2xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between" data-testid="dashboard-tmi-link">
              <div>
                <div className="eyebrow mb-1 text-sage">Also yours</div>
                <h3 className="font-serif-display text-xl text-ink">The Menopause Invitation</h3>
                <p className="mt-1 text-sm text-ink-soft max-w-md">
                  Your six-module program with auto-saving reflections — pick up wherever you left off.
                </p>
              </div>
              <Link
                to="/dashboard/menopause"
                className="inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium text-sm whitespace-nowrap"
                data-testid="dashboard-tmi-cta"
              >
                Open program
                <ArrowRight size={14} strokeWidth={1.8} />
              </Link>
            </div>
          )}

          {hasMensGuide && (
            <div className="mt-6 bg-cream-soft border border-hairline rounded-2xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between" data-testid="dashboard-mens-guide-link">
              <div>
                <div className="eyebrow mb-1 text-sage">Also yours</div>
                <h3 className="font-serif-display text-xl text-ink">A Man's Guide to Her Menopause</h3>
                <p className="mt-1 text-sm text-ink-soft max-w-md">
                  Six modules, auto-saving reflections — pick up wherever you left off.
                </p>
              </div>
              <Link
                to="/dashboard/mens-guide"
                className="inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium text-sm whitespace-nowrap"
                data-testid="dashboard-mens-guide-cta"
              >
                Open program
                <ArrowRight size={14} strokeWidth={1.8} />
              </Link>
            </div>
          )}

          <div className="mt-14">
            <div className="eyebrow mb-5 text-sage">Your Modules</div>
            {loading ? (
              <div className="text-ink-soft">Loading your program…</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {(modules || []).map((m, i) => (
                  <Link
                    key={m.id}
                    to={hasAccess ? `/dashboard/module/${m.id}` : "/the-rewired-life"}
                    className="group bg-white border border-hairline rounded-2xl p-8 hover:border-sage hover:shadow-[0_20px_40px_-20px_rgba(74,93,79,0.15)] transition-all"
                    data-testid={`dashboard-module-${m.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="eyebrow text-sage">{m.subtitle || `Module ${i}`}</div>
                      {!hasAccess && <Lock size={14} strokeWidth={1.5} className="text-ink-soft" />}
                    </div>
                    <h3 className="font-serif-display text-2xl text-ink leading-tight">{m.title}</h3>
                    <p className="mt-3 text-sm text-ink-soft leading-relaxed line-clamp-3">{m.description}</p>
                    <div className="mt-6 flex items-center gap-5 text-xs text-ink-soft">
                      <span className="inline-flex items-center gap-1.5"><PlayCircle size={12} strokeWidth={1.5} /> {m.duration || "—"}</span>
                      <span className="inline-flex items-center gap-1.5"><BookOpen size={12} strokeWidth={1.5} /> Workbook</span>
                      {(m.audio?.length || 0) > 0 && (
                        <span className="inline-flex items-center gap-1.5"><Headphones size={12} strokeWidth={1.5} /> Meditations</span>
                      )}
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-sage group-hover:translate-x-1 transition">
                      {hasAccess ? "Open module" : "Unlock"}
                      <ArrowRight size={14} strokeWidth={1.8} />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
